import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, MapPin, Tent, Mountain, Trophy, Heart, Briefcase, Sparkles,
  Users, Calendar, Compass, Plane, Quote,
} from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import BrandMonogramEdge from "@/components/BrandMonogramEdge";
import PageHero from "@/components/PageHero";
import PageStickyNav from "@/components/PageStickyNav";

const pick = (obj, lang) => {
  if (!obj) return "";
  if (typeof obj === "string") return obj;
  return obj[lang] || obj.en || obj.fr || "";
};

const HERO_IMAGE =
  "https://customer-assets.emergentagent.com/job_xaluca-luxury/artifacts/6pvzp897_Kasbah%20Hotel%20Xaluca%20-%20Ae%CC%81reas%20-%20HamidBabni-1.jpg";

const COPY = {
  // HERO
  hero_overline: { en: "Events in southern Morocco", fr: "Événements dans le sud du Maroc", es: "Eventos en el sur de Marruecos" },
  hero_title_1: { en: "Stage your event", fr: "Mettez en scène votre événement", es: "Diseñe su evento" },
  hero_title_2: { en: "where the desert meets the Atlas.", fr: "là où le désert rencontre l'Atlas.", es: "donde el desierto se encuentra con el Atlas." },
  hero_sub: {
    en: "From rally finish lines on the dunes of Erg Chebbi to weddings in the kasbah courtyards — Grup Xaluca designs end-to-end events across the south of Morocco.",
    fr: "Des arrivées de rallye sur les dunes de l'Erg Chebbi aux mariages dans les patios des kasbahs — le Grup Xaluca conçoit vos événements de bout en bout dans le sud du Maroc.",
    es: "De las metas de rally sobre las dunas del Erg Chebbi a las bodas en los patios de las kasbahs — Grup Xaluca diseña sus eventos de principio a fin en el sur de Marruecos.",
  },
  hero_cta_primary: { en: "Plan your event", fr: "Planifier votre événement", es: "Planificar su evento" },
  hero_cta_secondary: { en: "Download planning guide", fr: "Télécharger le guide", es: "Descargar la guía" },

  // INTRO / NUMBERS
  intro_title: {
    en: "An open-air stage of 200 000 km²",
    fr: "Une scène à ciel ouvert de 200 000 km²",
    es: "Un escenario al aire libre de 200 000 km²",
  },
  intro_text: {
    en: "Two ecosystems, one operator. Grup Xaluca owns and runs 11 properties across the Sahara, the High Atlas and the kasbah valleys — putting beds, kitchens, logistics and permits one phone call away. Whether you need to land a helicopter on a dune, set a tasting menu for 300 at 2 100 m of altitude, or pitch a hundred haimas in 48 hours, the answer is in-house.",
    fr: "Deux écosystèmes, un seul opérateur. Le Grup Xaluca possède et exploite 11 propriétés dans le Sahara, le Haut Atlas et les vallées des kasbahs — réunissant à portée d'un seul appel les lits, les cuisines, la logistique et les autorisations. Poser un hélicoptère sur une dune, dresser un menu dégustation pour 300 personnes à 2 100 m d'altitude ou monter cent haimas en 48 heures — la réponse est interne.",
    es: "Dos ecosistemas, un operador. Grup Xaluca opera 11 propiedades en el Sáhara, el Alto Atlas y los valles de las kasbahs — todo bajo una sola llamada: camas, cocinas, logística y permisos. Aterrizar un helicóptero en una duna, servir un menú degustación para 300 personas a 2 100 m o levantar cien haimas en 48 horas — la respuesta es interna.",
  },
  stats: [
    { num: "11", label: { en: "Properties operated", fr: "Propriétés opérées", es: "Propiedades gestionadas" } },
    { num: "2 000+", label: { en: "Guests catered nightly", fr: "Couverts par nuit", es: "Comensales por noche" } },
    { num: "30+", label: { en: "Years of expertise", fr: "Années d'expertise", es: "Años de experiencia" } },
    { num: "48h", label: { en: "Bivouac mobilisation", fr: "Montage de bivouac", es: "Montaje de vivac" } },
  ],

  // OPPORTUNITIES
  opps_overline: { en: "Opportunities", fr: "Opportunités", es: "Oportunidades" },
  opps_title: {
    en: "Six ways to fill the south of Morocco",
    fr: "Six façons d'animer le sud du Maroc",
    es: "Seis maneras de llenar el sur de Marruecos",
  },
  opps_sub: {
    en: "Pick the format, we'll build the production around it — venue, sleeping, catering, transport, talent and permits.",
    fr: "Choisissez le format, nous construisons la production autour — lieux, hébergement, traiteur, transport, talents et autorisations.",
    es: "Elija el formato y construimos la producción a su alrededor — sedes, alojamiento, catering, transporte, talento y permisos.",
  },
  opportunities: [
    {
      icon: Trophy,
      title: { en: "Adventure rallies", fr: "Rallyes d'aventure", es: "Rallies de aventura" },
      desc: {
        en: "Finish-line bivouacs for car, 4x4 and motorcycle rallies. Logistics partner of choice for events crossing the Erg Chebbi since the 90s.",
        fr: "Bivouacs d'arrivée pour rallyes auto, 4x4 et moto. Partenaire logistique des événements traversant l'Erg Chebbi depuis les années 90.",
        es: "Vivacs de meta para rallies de coches, 4x4 y motos. Socio logístico de los eventos que cruzan el Erg Chebbi desde los 90.",
      },
      image: "https://images.pexels.com/photos/2356045/pexels-photo-2356045.jpeg?auto=compress&cs=tinysrgb&w=1200",
    },
    {
      icon: Briefcase,
      title: { en: "Corporate retreats", fr: "Séminaires d'entreprise", es: "Retiros corporativos" },
      desc: {
        en: "Conferences, conventions, off-sites and leadership programmes in private kasbahs, with breakout rooms in palm groves and 4x4 challenges in the dunes.",
        fr: "Conférences, conventions, off-sites et programmes leadership dans des kasbahs privatives — salles de breakout en palmeraie et défis 4x4 dans les dunes.",
        es: "Conferencias, convenciones, off-sites y programas de liderazgo en kasbahs privadas, con breakouts en palmerales y desafíos 4x4 en las dunas.",
      },
      image: "https://customer-assets.emergentagent.com/job_xaluca-luxury/artifacts/4p3qhcbe_Kasbah%20Hotel%20Xaluca%20-%20Ae%CC%81reas%20-%20HamidBabni-3.jpg",
    },
    {
      icon: Heart,
      title: { en: "Luxury weddings", fr: "Mariages d'exception", es: "Bodas de excepción" },
      desc: {
        en: "Three-day celebrations between kasbah ceremony, dune sunset reception and oasis brunch. Henna, qraqeb, traditional Aïssawa music — or a string quartet at altitude.",
        fr: "Célébrations sur trois jours : cérémonie à la kasbah, réception au coucher du soleil dans les dunes, brunch en oasis. Henné, qraqeb, musique aïssawa — ou un quatuor à cordes en altitude.",
        es: "Celebraciones de tres días: ceremonia en kasbah, recepción al atardecer en las dunas, brunch en oasis. Henna, qraqeb, música Aïssawa — o un cuarteto de cuerda en altura.",
      },
      image: "https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&w=1200&q=80",
    },
    {
      icon: Sparkles,
      title: { en: "Brand launches & film", fr: "Lancements & tournages", es: "Lanzamientos y rodajes" },
      desc: {
        en: "Cinematic backdrops — UNESCO ksars, lake-side spas, hammada plateaus. Closed-set permits, helicopter access, generator and base-camp infrastructure.",
        fr: "Décors cinématographiques — ksars UNESCO, spas au bord du lac, plateaux de hamada. Autorisations plateau fermé, accès hélicoptère, groupe électrogène et camps de base.",
        es: "Escenarios cinematográficos — ksars UNESCO, spas a orillas del lago, mesetas de hamada. Permisos de plató cerrado, acceso en helicóptero, generadores y campamentos base.",
      },
      image: "https://customer-assets.emergentagent.com/job_xaluca-luxury/artifacts/qrtoq576_Kasbah%20Hotel%20Xaluca%20-%20Ae%CC%81reas%20-%20HamidBabni-5.jpg",
    },
    {
      icon: Users,
      title: { en: "Cultural festivals", fr: "Festivals culturels", es: "Festivales culturales" },
      desc: {
        en: "Multi-day festivals for music, gastronomy, astronomy or literature. Stage, sound, lights, hospitality villages and 1 000-seat bivouacs.",
        fr: "Festivals de plusieurs jours : musique, gastronomie, astronomie, littérature. Scène, son, lumières, villages d'accueil et bivouacs de 1 000 places.",
        es: "Festivales de varios días: música, gastronomía, astronomía, literatura. Escenario, sonido, luces, villas de hospitalidad y vivacs de 1 000 plazas.",
      },
      image: "https://images.unsplash.com/photo-1519677100203-a0e668c92439?auto=format&fit=crop&w=1200&q=80",
    },
    {
      icon: Compass,
      title: { en: "Incentive travel", fr: "Voyages incentive", es: "Viajes de incentivo" },
      desc: {
        en: "Trophy trips that pair adventure (camel caravans, dune racing, M'Goun trekking) with five-star kasbah evenings and chef tables for the team.",
        fr: "Voyages d'incentive alliant aventure (caravane de chameaux, sortie dunes, trek M'Goun) et soirées kasbah cinq étoiles avec tables du chef pour l'équipe.",
        es: "Viajes incentivo que combinan aventura (caravana de camellos, dunas, trek M'Goun) con veladas en kasbah cinco estrellas y mesa del chef para el equipo.",
      },
      image: "https://images.pexels.com/photos/30374217/pexels-photo-30374217.jpeg?auto=compress&cs=tinysrgb&w=1200",
    },
  ],

  // REGIONS
  regions_overline: { en: "Regional highlights", fr: "Régions phares", es: "Regiones destacadas" },
  regions_title: {
    en: "Two backdrops, one production team",
    fr: "Deux décors, une seule équipe de production",
    es: "Dos escenarios, un solo equipo de producción",
  },
  desert: {
    eyebrow: { en: "The Desert", fr: "Le Désert", es: "El Desierto" },
    title: { en: "Sahara · Erg Chebbi · Merzouga", fr: "Sahara · Erg Chebbi · Merzouga", es: "Sáhara · Erg Chebbi · Merzouga" },
    text: {
      en: "Three hundred metres of golden dunes, sub-Saharan villages, palm groves and a sky that is one of the darkest in the world. Our four bivouacs can be combined to host from 12 guests in private haimas to a 1 200-pax rally finish.",
      fr: "Trois cents mètres de dunes dorées, villages sub-sahariens, palmeraies et l'un des ciels les plus sombres du monde. Nos quatre bivouacs se combinent pour accueillir de 12 invités sous haimas privées à une arrivée de rallye de 1 200 personnes.",
      es: "Trescientos metros de dunas doradas, aldeas subsaharianas, palmerales y uno de los cielos más oscuros del mundo. Nuestros cuatro vivacs se combinan para acoger de 12 invitados en haimas privadas a una meta de rally de 1 200 personas.",
    },
    suitable: [
      { en: "Rally finish lines & vehicle expos", fr: "Arrivées de rallye & expos véhicules", es: "Metas de rally y expos de vehículos" },
      { en: "Dune weddings & sunset receptions", fr: "Mariages sur dune & réceptions sunset", es: "Bodas en dunas y recepciones al atardecer" },
      { en: "Brand films & fashion shoots", fr: "Films de marque & shooting mode", es: "Films de marca y shootings de moda" },
      { en: "Astronomy & meditation retreats", fr: "Retraites astronomie & méditation", es: "Retiros de astronomía y meditación" },
    ],
    image: "https://images.pexels.com/photos/30374217/pexels-photo-30374217.jpeg?auto=compress&cs=tinysrgb&w=1600",
    stat_label: { en: "Max capacity", fr: "Capacité maximale", es: "Capacidad máxima" },
    stat_value: { en: "1 200 guests", fr: "1 200 invités", es: "1 200 invitados" },
  },
  atlas: {
    eyebrow: { en: "The Atlas Mountains", fr: "L'Atlas marocain", es: "El Atlas marroquí" },
    title: { en: "High Atlas · Dades · Aguelmame Sidi Ali", fr: "Haut Atlas · Dades · Aguelmame Sidi Ali", es: "Alto Atlas · Dades · Aguelmame Sidi Ali" },
    text: {
      en: "From the crimson Dades canyons to the 2 100-metre cedar plateau around Lake Aguelmame Sidi Ali. Mountain receptions, lake-edge ceremonies and adventure tracks for 4x4, mountain bike or mule. Cool summers, snowy winters.",
      fr: "Des canyons pourpres du Dades au plateau cédraie à 2 100 m autour du lac Aguelmame Sidi Ali. Réceptions en altitude, cérémonies au bord du lac, pistes 4x4, VTT et mule. Étés frais, hivers enneigés.",
      es: "De los cañones púrpuras del Dades a la meseta de cedros a 2 100 m alrededor del lago Aguelmame Sidi Ali. Recepciones en altura, ceremonias junto al lago, pistas 4x4, MTB y mula. Veranos frescos, inviernos nevados.",
    },
    suitable: [
      { en: "Altitude conferences & off-sites", fr: "Conférences & off-sites en altitude", es: "Conferencias y off-sites en altitud" },
      { en: "Mountain weddings with cedar forest backdrop", fr: "Mariages avec cédraie en toile de fond", es: "Bodas con bosque de cedros como fondo" },
      { en: "Trail-running & mountain-bike events", fr: "Trail running & événements VTT", es: "Trail running y eventos de MTB" },
      { en: "Wellness, yoga & stargazing retreats", fr: "Retraites bien-être, yoga & étoiles", es: "Retiros de bienestar, yoga y estrellas" },
    ],
    image: "https://customer-assets.emergentagent.com/job_xaluca-luxury/artifacts/htd6p1fu_Kasbah%20Hotel%20Xaluca%20-%20Ae%CC%81reas%20-%20HamidBabni-4.jpg",
    stat_label: { en: "Altitude", fr: "Altitude", es: "Altitud" },
    stat_value: { en: "Up to 2 100 m", fr: "Jusqu'à 2 100 m", es: "Hasta 2 100 m" },
  },

  // CASE STUDIES
  cases_overline: { en: "Case studies", fr: "Études de cas", es: "Casos de éxito" },
  cases_title: {
    en: "A few of the stories we've helped stage",
    fr: "Quelques histoires que nous avons aidé à mettre en scène",
    es: "Algunas historias que hemos ayudado a poner en escena" ,
  },
  cases: [
    {
      tag: { en: "Adventure rally", fr: "Rallye d'aventure", es: "Rally de aventura" },
      title: { en: "Sahara Endurance Rally · Erg Chebbi", fr: "Rallye Sahara Endurance · Erg Chebbi", es: "Rally Sahara Endurance · Erg Chebbi" },
      quote: {
        en: "“They built a 600-vehicle service area and a 1 000-cover dinner — on sand, on time, on budget.”",
        fr: "« Ils ont monté une zone de service de 600 véhicules et un dîner pour 1 000 personnes — sur le sable, à l'heure, dans le budget. »",
        es: "«Montaron una zona de servicio para 600 vehículos y una cena para 1 000 personas — sobre arena, a tiempo y dentro del presupuesto.»",
      },
      author: "Race Director · Sahara Endurance",
      stat: { en: "1 000 covers", fr: "1 000 couverts", es: "1 000 comensales" },
    },
    {
      tag: { en: "Tech off-site", fr: "Off-site Tech", es: "Off-site Tech" },
      title: { en: "European SaaS · 220-person off-site", fr: "SaaS européen · off-site 220 personnes", es: "SaaS europeo · off-site 220 personas" },
      quote: {
        en: "“Three days, three properties — kasbah workshops, dune dinner, lake-side closing. Logistics felt invisible. Our team did not.”",
        fr: "« Trois jours, trois lieux — ateliers en kasbah, dîner sur dunes, clôture au bord du lac. La logistique était invisible. Notre équipe non. »",
        es: "«Tres días, tres lugares — talleres en kasbah, cena en duna, cierre junto al lago. La logística fue invisible. Nuestro equipo no.»",
      },
      author: "VP People · European SaaS",
      stat: { en: "3 properties", fr: "3 propriétés", es: "3 propiedades" },
    },
    {
      tag: { en: "Destination wedding", fr: "Mariage destination", es: "Boda destino" },
      title: { en: "A four-day wedding · Arfoud & dunes", fr: "Mariage en 4 jours · Arfoud & dunes", es: "Boda de 4 días · Arfoud y dunas" },
      quote: {
        en: "“They turned a kasbah into a chapel and a dune into a dance floor — and remembered our parents' dietary restrictions.”",
        fr: "« Ils ont transformé une kasbah en chapelle et une dune en piste de danse — et se sont souvenus des régimes de nos parents. »",
        es: "«Convirtieron una kasbah en capilla y una duna en pista de baile — y recordaron las dietas de nuestros padres.»",
      },
      author: "Wedding couple · London + Paris",
      stat: { en: "180 guests · 4 days", fr: "180 invités · 4 jours", es: "180 invitados · 4 días" },
    },
  ],

  // PROCESS
  process_overline: { en: "How it works", fr: "Comment ça se passe", es: "Cómo funciona" },
  process_title: {
    en: "From first call to final transfer",
    fr: "Du premier appel au dernier transfert",
    es: "De la primera llamada al último traslado",
  },
  process_steps: [
    { num: "01", title: { en: "Brief & site selection", fr: "Brief & sélection des lieux", es: "Brief y selección de sedes" }, text: { en: "Discovery call, objectives, headcount, dates. We send a custom location dossier within 5 days.", fr: "Appel découverte, objectifs, effectifs, dates. Dossier de lieux personnalisé sous 5 jours.", es: "Llamada de descubrimiento, objetivos, asistentes, fechas. Dossier de sedes en 5 días." } },
    { num: "02", title: { en: "Production design", fr: "Conception production", es: "Diseño de producción" }, text: { en: "Storyboard, F&B, talent, AV, transport, permits, risk. One quote, all-inclusive.", fr: "Storyboard, restauration, talents, audiovisuel, transport, autorisations, sécurité. Un seul devis, tout compris.", es: "Storyboard, F&B, talento, AV, transporte, permisos, riesgo. Una cotización, todo incluido." } },
    { num: "03", title: { en: "On the ground", fr: "Sur le terrain", es: "Sobre el terreno" }, text: { en: "Dedicated event director, 60+ in-house staff, redundancy across properties, 24/7 control room.", fr: "Directeur d'événement dédié, plus de 60 collaborateurs internes, redondance multi-sites, PC 24/7.", es: "Director de evento dedicado, +60 personas internas, redundancia multi-sede, sala de control 24/7." } },
    { num: "04", title: { en: "Debrief & legacy", fr: "Debrief & héritage", es: "Debriefing y legado" }, text: { en: "Post-event report, photo & film deliverables, NPS survey, CSR fund for local artisans we worked with.", fr: "Rapport post-événement, livrables photo & film, NPS, fonds RSE pour les artisans locaux mobilisés.", es: "Informe post-evento, entregables foto y vídeo, NPS, fondo RSC para los artesanos locales colaboradores." } },
  ],

  // FINAL CTA
  cta_title: {
    en: "Let's build the next chapter together",
    fr: "Écrivons ensemble le prochain chapitre",
    es: "Construyamos juntos el próximo capítulo",
  },
  cta_text: {
    en: "Tell us the format, the headcount and the dates — we will come back with a venue dossier and a single all-inclusive quote.",
    fr: "Indiquez-nous le format, les effectifs et les dates — nous revenons avec un dossier de lieux et un devis unique tout compris.",
    es: "Indíquenos el formato, los asistentes y las fechas — le devolveremos un dossier de sedes y una única cotización todo incluido.",
  },
  cta_primary: { en: "Request a proposal", fr: "Demander une proposition", es: "Solicitar una propuesta" },
  cta_secondary: { en: "Talk to the events team", fr: "Parler à l'équipe événements", es: "Hablar con el equipo de eventos" },
};

const EventsPage = () => {
  const { lang } = useLanguage();

  return (
    <div data-testid="page-events" className="bg-[#FDFBF7]">
      {/* HERO — multi-slide (video first, then events photography) */}
      <section data-testid="events-hero">
        <PageHero
          images={[
            HERO_IMAGE,
            "https://images.unsplash.com/photo-1518684079-3c830dcef090?auto=format&fit=crop&w=2400&q=80",
            "https://customer-assets.emergentagent.com/job_xaluca-luxury/artifacts/htd6p1fu_Kasbah%20Hotel%20Xaluca%20-%20Ae%CC%81reas%20-%20HamidBabni-4.jpg",
          ]}
          alt="Events · Grup Xaluca"
          scope="page.events"
          overline={pick(COPY.hero_overline, lang)}
          title={pick(COPY.hero_title_1, lang)}
          titleAccent={pick(COPY.hero_title_2, lang)}
          sub={pick(COPY.hero_sub, lang)}
          ctaTo="/contact?subject=Events"
          ctaLabel={pick(COPY.hero_cta_primary, lang)}
          secondaryTo="/contact?subject=Events"
          secondaryLabel={pick(COPY.hero_cta_secondary, lang)}
        />
      </section>

      <PageStickyNav
        items={[
          { id: "events-intro", label: { en: "Intro", fr: "Intro", es: "Intro" } },
          { id: "events-conference-highlight", label: { en: "Sala", fr: "Salle", es: "Sala" } },
          { id: "events-opportunities", label: { en: "Spaces", fr: "Espaces", es: "Espacios" } },
          { id: "events-regions", label: { en: "Regions", fr: "Régions", es: "Regiones" } },
          { id: "events-cases", label: { en: "Cases", fr: "Cas", es: "Casos" } },
          { id: "events-process", label: { en: "Process", fr: "Processus", es: "Proceso" } },
          { id: "events-deep-dives", label: { en: "Deep dives", fr: "Focus", es: "Detalle" } },
          { id: "events-clients", label: { en: "Clients", fr: "Clients", es: "Clientes" } },
          { id: "events-services", label: { en: "Services", fr: "Services", es: "Servicios" } },
          { id: "events-final-cta", label: { en: "Contact", fr: "Contact", es: "Contacto" } },
        ]}
      />

      {/* CONFERENCE-ROOM HIGHLIGHT — the best of the south */}
      <section id="events-conference-highlight" className="py-24 md:py-32 bg-[#F2EBE1]/60 border-y border-[#2C2621]/10 scroll-mt-32" data-testid="events-conference-highlight" data-cms-scope="events.conference-room">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-5">
            <p className="overline">
              {pick({ en: "Discover what Grup Xaluca can offer you", fr: "Découvrez ce que Grup Xaluca peut vous offrir", es: "Descubra lo que Grup Xaluca puede ofrecerle" }, lang)}
            </p>
            <h2 className="font-serif-x mt-4 text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-[#2C2621]">
              {pick({
                en: "The best conference room in southern Morocco.",
                fr: "La meilleure salle de conférences du sud du Maroc.",
                es: "La mejor sala de conferencias del sur de Marruecos.",
              }, lang)}
            </h2>
            <p className="mt-6 text-lg text-[#5C5248] leading-relaxed">
              {pick({
                en: "One of our most significant advantages — the best-equipped conference room in the south of the country, framed by the panorama of the great Erg Chebbi dunes.",
                fr: "L'un de nos atouts majeurs — la salle de conférences la mieux équipée du sud du pays, ouverte sur le panorama des grandes dunes de l'Erg Chebbi.",
                es: "Una de nuestras mayores ventajas — la sala de conferencias mejor equipada del sur del país, enmarcada por el panorama de las grandes dunas del Erg Chebbi.",
              }, lang)}
            </p>
            <p className="mt-5 text-base text-[#5C5248] leading-relaxed">
              {pick({
                en: "Corporate events, big celebrations, full event production — the Grup Xaluca events division partners with event agencies and brands to create experiences only the south of Morocco can host.",
                fr: "Événements d'entreprise, grandes célébrations, production événementielle de A à Z — la division événements du Grup Xaluca accompagne agences et marques pour créer des expériences que seul le sud du Maroc peut offrir.",
                es: "Eventos corporativos, grandes celebraciones, producción de eventos completa — la división de eventos de Grup Xaluca acompaña a agencias y marcas para crear experiencias que solo el sur de Marruecos puede albergar.",
              }, lang)}
            </p>
            <div className="mt-10 flex flex-wrap gap-2.5" data-testid="events-categories">
              {[
                { en: "Cultural Festivals", fr: "Festivals culturels", es: "Festivales culturales" },
                { en: "Sporting Events", fr: "Événements sportifs", es: "Eventos deportivos" },
                { en: "Rallies & Competitions", fr: "Rallyes & Compétitions", es: "Rallies y Competiciones" },
                { en: "Meetings & Incentives", fr: "Réunions & Incentives", es: "Reuniones e Incentivos" },
                { en: "Weddings & Celebrations", fr: "Mariages & Célébrations", es: "Bodas y Celebraciones" },
                { en: "Conferences & Workshops", fr: "Conférences & Workshops", es: "Conferencias y Talleres" },
              ].map((c, i) => (
                <span
                  key={`events-cat-${c.en}`}
                  className="text-[10px] tracking-[0.25em] uppercase px-3 py-1.5 rounded-full border border-[#C16542]/40 text-[#C16542] bg-white/60"
                  data-testid={`events-category-${i}`}
                >
                  {pick(c, lang)}
                </span>
              ))}
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="relative aspect-[4/3] overflow-hidden">
              <img
                src="https://customer-assets.emergentagent.com/job_xaluca-luxury/artifacts/qrtoq576_Kasbah%20Hotel%20Xaluca%20-%20Ae%CC%81reas%20-%20HamidBabni-5.jpg"
                alt="Conference room — Kasbah Hotel Xaluca"
                className="absolute inset-0 w-full h-full object-cover" loading="lazy" decoding="async"
                data-cms-role="hero"/>
              <div className="absolute inset-0 bg-gradient-to-t from-[#1A1513]/55 via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 text-[#FDFBF7] flex items-end justify-between gap-4">
                <div>
                  <p className="text-[10px] tracking-[0.3em] uppercase text-[#D4A373]">
                    {pick({ en: "Capacity", fr: "Capacité", es: "Capacidad" }, lang)}
                  </p>
                  <p className="font-serif-x text-3xl md:text-4xl mt-1">
                    {pick({ en: "300 seats · theatre", fr: "300 places · théâtre", es: "300 plazas · teatro" }, lang)}
                  </p>
                </div>
                <div className="text-right hidden sm:block">
                  <p className="text-[10px] tracking-[0.3em] uppercase text-[#D4A373]">
                    {pick({ en: "Languages", fr: "Langues", es: "Idiomas" }, lang)}
                  </p>
                  <p className="font-serif-x text-2xl mt-1">
                    {pick({ en: "4 simultaneous", fr: "4 simultanées", es: "4 simultáneas" }, lang)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* INTRO + STATS */}
      <section id="events-intro" className="py-20 md:py-28 scroll-mt-32" data-testid="events-intro">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-7">
            <p className="overline">
              {pick({ en: "Why southern Morocco", fr: "Pourquoi le sud du Maroc", es: "Por qué el sur de Marruecos" }, lang)}
            </p>
            <h2 className="font-serif-x mt-4 text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
              {pick(COPY.intro_title, lang)}
            </h2>
            <p className="mt-8 text-lg md:text-xl text-[#5C5248] leading-relaxed">
              {pick(COPY.intro_text, lang)}
            </p>
          </div>
          <div className="md:col-span-5 grid grid-cols-2 gap-x-6 gap-y-10 md:pl-8 md:border-l md:border-[#2C2621]/10 md:items-center">
            {COPY.stats.map((s, i) => (
              <div key={`stat-${i}`} data-testid={`events-stat-${i}`}>
                <div className="font-serif-x text-5xl md:text-6xl text-[#C16542] tracking-tight leading-none">
                  {s.num}
                </div>
                <p className="mt-3 overline !text-[#5C5248]">{pick(s.label, lang)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* OPPORTUNITIES */}
      <section className="bg-[#1A1513] text-[#FDFBF7] py-24 md:py-32" data-testid="events-opportunities">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-12 gap-8 mb-16">
            <div className="md:col-span-6">
              <p className="overline !text-[#D4A373]">{pick(COPY.opps_overline, lang)}</p>
              <h2 className="font-serif-x mt-4 text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
                {pick(COPY.opps_title, lang)}
              </h2>
            </div>
            <div className="md:col-span-5 md:col-start-8 flex items-end">
              <p className="text-lg text-[#FDFBF7]/75 leading-relaxed">{pick(COPY.opps_sub, lang)}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {COPY.opportunities.map((op, i) => {
              const Icon = op.icon;
              return (
                <article
                  key={`op-${i}`}
                  className="group relative overflow-hidden aspect-[4/5] border border-[#FDFBF7]/10"
                  data-testid={`events-opportunity-${i}`}
                  data-cms-scope={`events.opportunity.${i}`}
                >
                  <img
                    src={op.image}
                    alt={pick(op.title, lang)}
                    className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-90 transition-opacity duration-700" loading="lazy" decoding="async"
                    data-cms-role="card.image"/>
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1A1513] via-[#1A1513]/65 to-[#1A1513]/15" />
                  <div className="absolute inset-0 p-6 md:p-8 flex flex-col justify-end">
                    <div className="w-11 h-11 rounded-full bg-[#D4A373]/15 text-[#D4A373] flex items-center justify-center">
                      <Icon className="w-5 h-5" strokeWidth={1.5} />
                    </div>
                    <h3 className="font-serif-x text-2xl md:text-3xl mt-5 leading-tight">
                      {pick(op.title, lang)}
                    </h3>
                    <p className="mt-3 text-sm text-[#FDFBF7]/80 leading-relaxed">
                      {pick(op.desc, lang)}
                    </p>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* REGIONAL HIGHLIGHTS */}
      <section id="events-regions" className="py-24 md:py-32 scroll-mt-32" data-testid="events-regions">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <p className="overline">{pick(COPY.regions_overline, lang)}</p>
            <h2 className="font-serif-x mt-4 text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
              {pick(COPY.regions_title, lang)}
            </h2>
          </div>

          {/* DESERT */}
          <div className="grid md:grid-cols-12 gap-10 md:gap-12 items-center mb-20 md:mb-28" data-testid="events-region-desert" data-cms-scope="events.region.desert">
            <div className="md:col-span-7">
              <div className="relative aspect-[4/3] overflow-hidden">
                <img src={COPY.desert.image} alt="Sahara" className="absolute inset-0 w-full h-full object-cover" loading="lazy" decoding="async" data-cms-role="card.image"/>
                <div className="absolute inset-0 bg-gradient-to-t from-[#1A1513]/40 to-transparent" />
                <div className="absolute bottom-6 left-6 right-6 text-[#FDFBF7] flex items-end justify-between gap-4">
                  <div className="flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-[#D4A373]">
                    <Tent className="w-3.5 h-3.5" strokeWidth={1.5} /> {pick(COPY.desert.eyebrow, lang)}
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] tracking-[0.25em] uppercase text-[#FDFBF7]/70">{pick(COPY.desert.stat_label, lang)}</p>
                    <p className="font-serif-x text-2xl">{pick(COPY.desert.stat_value, lang)}</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="md:col-span-5">
              <p className="overline text-[#C16542]">{pick(COPY.desert.eyebrow, lang)}</p>
              <h3 className="font-serif-x mt-3 text-3xl md:text-4xl lg:text-5xl tracking-tight leading-[1.05]">
                {pick(COPY.desert.title, lang)}
              </h3>
              <p className="mt-6 text-lg text-[#5C5248] leading-relaxed">{pick(COPY.desert.text, lang)}</p>
              <p className="overline mt-10">
                {pick({ en: "Best suited for", fr: "Idéal pour", es: "Ideal para" }, lang)}
              </p>
              <ul className="mt-4 grid grid-cols-1 gap-2.5 text-sm text-[#2C2621]">
                {COPY.desert.suitable.map((s, i) => (
                  <li key={`d-${i}`} className="flex items-start gap-2.5">
                    <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#C16542] shrink-0" />
                    <span>{pick(s, lang)}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* ATLAS */}
          <div className="grid md:grid-cols-12 gap-10 md:gap-12 items-center" data-testid="events-region-atlas" data-cms-scope="events.region.atlas">
            <div className="md:col-span-5 md:order-1 order-2">
              <p className="overline" style={{ color: "#3F5C7E" }}>{pick(COPY.atlas.eyebrow, lang)}</p>
              <h3 className="font-serif-x mt-3 text-3xl md:text-4xl lg:text-5xl tracking-tight leading-[1.05]">
                {pick(COPY.atlas.title, lang)}
              </h3>
              <p className="mt-6 text-lg text-[#5C5248] leading-relaxed">{pick(COPY.atlas.text, lang)}</p>
              <p className="overline mt-10">
                {pick({ en: "Best suited for", fr: "Idéal pour", es: "Ideal para" }, lang)}
              </p>
              <ul className="mt-4 grid grid-cols-1 gap-2.5 text-sm text-[#2C2621]">
                {COPY.atlas.suitable.map((s, i) => (
                  <li key={`a-${i}`} className="flex items-start gap-2.5">
                    <span className="mt-2 w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: "#3F5C7E" }} />
                    <span>{pick(s, lang)}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="md:col-span-7 md:order-2 order-1">
              <div className="relative aspect-[4/3] overflow-hidden">
                <img src={COPY.atlas.image} alt="Atlas Mountains" className="absolute inset-0 w-full h-full object-cover" loading="lazy" decoding="async" data-cms-role="card.image"/>
                <div className="absolute inset-0 bg-gradient-to-t from-[#1A1513]/40 to-transparent" />
                <div className="absolute bottom-6 left-6 right-6 text-[#FDFBF7] flex items-end justify-between gap-4">
                  <div className="flex items-center gap-2 text-xs tracking-[0.25em] uppercase" style={{ color: "#A8C2DD" }}>
                    <Mountain className="w-3.5 h-3.5" strokeWidth={1.5} /> {pick(COPY.atlas.eyebrow, lang)}
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] tracking-[0.25em] uppercase text-[#FDFBF7]/70">{pick(COPY.atlas.stat_label, lang)}</p>
                    <p className="font-serif-x text-2xl">{pick(COPY.atlas.stat_value, lang)}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CASE STUDIES */}
      <section id="events-cases" className="bg-[#F2EBE1]/60 py-24 md:py-32 border-y border-[#2C2621]/10 scroll-mt-32" data-testid="events-cases">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-12 gap-8 mb-16">
            <div className="md:col-span-7">
              <p className="overline">{pick(COPY.cases_overline, lang)}</p>
              <h2 className="font-serif-x mt-4 text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
                {pick(COPY.cases_title, lang)}
              </h2>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            {COPY.cases.map((cs, i) => (
              <figure
                key={`case-${i}`}
                className="bg-[#FDFBF7] border border-[#2C2621]/10 p-8 md:p-10 flex flex-col"
                data-testid={`events-case-${i}`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-[10px] tracking-[0.25em] uppercase text-[#C16542] border border-[#C16542]/30 px-2.5 py-1">
                    {pick(cs.tag, lang)}
                  </span>
                  <span className="text-xs tracking-[0.2em] uppercase text-[#5C5248]/70">{pick(cs.stat, lang)}</span>
                </div>
                <h3 className="font-serif-x text-2xl md:text-3xl mt-6 leading-tight text-[#2C2621]">
                  {pick(cs.title, lang)}
                </h3>
                <Quote className="w-8 h-8 mt-6 text-[#C16542]/60" strokeWidth={1.5} />
                <blockquote className="mt-3 font-serif-x italic text-lg leading-snug text-[#2C2621] flex-1">
                  {pick(cs.quote, lang)}
                </blockquote>
                <figcaption className="mt-6 text-xs tracking-[0.2em] uppercase text-[#5C5248]">
                  — {cs.author}
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section id="events-process" className="py-24 md:py-32 scroll-mt-32" data-testid="events-process">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-12 gap-8 mb-16">
            <div className="md:col-span-6">
              <p className="overline">{pick(COPY.process_overline, lang)}</p>
              <h2 className="font-serif-x mt-4 text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
                {pick(COPY.process_title, lang)}
              </h2>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-12">
            {COPY.process_steps.map((st, i) => (
              <div key={`step-${i}`} className="border-t border-[#2C2621]/15 pt-5" data-testid={`events-step-${i}`}>
                <div className="flex items-baseline justify-between">
                  <span className="font-serif-x text-3xl text-[#C16542]">{st.num}</span>
                  <span className="text-[10px] tracking-[0.3em] uppercase text-[#5C5248]/60">
                    {i === 0 && <Calendar className="w-3.5 h-3.5 inline" strokeWidth={1.5} />}
                    {i === 1 && <Compass className="w-3.5 h-3.5 inline" strokeWidth={1.5} />}
                    {i === 2 && <Users className="w-3.5 h-3.5 inline" strokeWidth={1.5} />}
                    {i === 3 && <Plane className="w-3.5 h-3.5 inline" strokeWidth={1.5} />}
                  </span>
                </div>
                <h3 className="font-serif-x text-2xl mt-4 leading-tight text-[#2C2621]">{pick(st.title, lang)}</h3>
                <p className="mt-3 text-sm text-[#5C5248] leading-relaxed">{pick(st.text, lang)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* EDITORIAL DEEP-DIVES — 4 dedicated blocks */}
      <section id="events-deep-dives" className="py-24 md:py-32 bg-[#FBF6EE]/70 scroll-mt-32" data-testid="events-deep-dives">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-12 gap-8 mb-14 items-end">
            <div className="md:col-span-7">
              <p className="overline">
                {pick({ en: "Grup Xaluca · Events", fr: "Grup Xaluca · Événements", es: "Grup Xaluca · Eventos" }, lang)}
              </p>
              <h2 className="font-serif-x mt-4 text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-[#2C2621]">
                {pick({
                  en: "Four worlds we know inside out.",
                  fr: "Quatre univers que nous connaissons par cœur.",
                  es: "Cuatro universos que conocemos al dedillo.",
                }, lang)}
              </h2>
            </div>
            <div className="md:col-span-5">
              <p className="text-base text-[#5C5248] leading-relaxed">
                {pick({
                  en: "From the Marathon des Sables logistics base to a corporate convention for Heineken — we have produced them all, and we can build yours next.",
                  fr: "De la base logistique du Marathon des Sables à une convention d'entreprise pour Heineken — nous les avons tous produits, et le vôtre est le prochain.",
                  es: "De la base logística del Marathon des Sables a una convención corporativa para Heineken — los hemos producido todos, y el suyo es el siguiente.",
                }, lang)}
              </p>
            </div>
          </div>

          <div className="space-y-12 md:space-y-16">
            {[
              {
                id: "festivals",
                tag: { en: "Festivals", fr: "Festivals", es: "Festivales" },
                title: { en: "Cultural festivals across the south", fr: "Festivals culturels dans tout le sud", es: "Festivales culturales por todo el sur" },
                text: {
                  en: "Grup Xaluca has the infrastructure and the organisation skills to stage any kind of festival in the south of Morocco — music, gastronomy, astronomy, literature, film. Whatever you have in mind, our team will work hand-in-hand with yours until the doors open.",
                  fr: "Le Grup Xaluca possède l'infrastructure et le savoir-faire pour organiser n'importe quel festival dans le sud du Maroc — musique, gastronomie, astronomie, littérature, cinéma. Quel que soit votre projet, notre équipe avancera main dans la main avec la vôtre jusqu'à l'ouverture des portes.",
                  es: "Grup Xaluca cuenta con la infraestructura y la experiencia para organizar cualquier festival en el sur de Marruecos — música, gastronomía, astronomía, literatura, cine. Sea cual sea su idea, nuestro equipo trabajará codo con codo con el suyo hasta el día de la inauguración.",
                },
                image: "https://images.unsplash.com/photo-1519677100203-a0e668c92439?auto=format&fit=crop&w=1800&q=80",
                reverse: false,
                badges: [
                  { en: "Multi-day staging", fr: "Plateaux multi-jours", es: "Montaje multi-día" },
                  { en: "Sound · light · talent", fr: "Son · lumière · talents", es: "Sonido · luz · talento" },
                  { en: "1 000-seat bivouacs", fr: "Bivouacs de 1 000 places", es: "Vivacs de 1 000 plazas" },
                ],
              },
              {
                id: "sporting",
                tag: { en: "Sporting Events", fr: "Événements sportifs", es: "Eventos deportivos" },
                title: { en: "Where the world's toughest races come home", fr: "Le terrain des courses les plus exigeantes du monde", es: "El terreno de las carreras más exigentes del mundo" },
                text: {
                  en: "Our establishments and logistics infrastructure sit at the heart of a region that hosts some of the most demanding international competitions on Earth — the Garmin Titan Desert, the Marathon des Sables, the Merzouga Rally, the Desert Run, and many more. Grup Xaluca provides the logistics for all of them.",
                  fr: "Nos établissements et notre logistique sont au cœur d'une région qui accueille les compétitions internationales les plus exigeantes — la Garmin Titan Desert, le Marathon des Sables, le Rallye de Merzouga, la Desert Run, et bien d'autres. Le Grup Xaluca fournit la logistique de chacune d'elles.",
                  es: "Nuestros establecimientos y nuestra logística están en el corazón de una región que acoge las competiciones internacionales más exigentes — Garmin Titan Desert, Marathon des Sables, Rally de Merzouga, Desert Run, entre muchas otras. Grup Xaluca proporciona la logística de todas ellas.",
                },
                image: "https://images.pexels.com/photos/2356045/pexels-photo-2356045.jpeg?auto=compress&cs=tinysrgb&w=1800",
                reverse: true,
                badges: [
                  { en: "Garmin Titan Desert", fr: "Garmin Titan Desert", es: "Garmin Titan Desert" },
                  { en: "Marathon des Sables", fr: "Marathon des Sables", es: "Marathon des Sables" },
                  { en: "Merzouga Rally", fr: "Rallye de Merzouga", es: "Rally de Merzouga" },
                  { en: "Desert Run", fr: "Desert Run", es: "Desert Run" },
                ],
              },
              {
                id: "business",
                tag: { en: "Business Events", fr: "Événements d'entreprise", es: "Eventos corporativos" },
                title: { en: "The destination chosen by the brands you know", fr: "La destination choisie par les marques que vous connaissez", es: "El destino elegido por las marcas que conoce" },
                text: {
                  en: "In recent years, southern Morocco has become the destination chosen by thousands of companies, from every sector, to host their corporate events. International brands — BMW, Vodafone, Volkswagen, Coca-Cola, IBM, Heineken — have already trusted Grup Xaluca to stage theirs in the heart of the desert.",
                  fr: "Ces dernières années, le sud du Maroc est devenu la destination choisie par des milliers d'entreprises de tous secteurs pour leurs événements corporate. Des marques internationales — BMW, Vodafone, Volkswagen, Coca-Cola, IBM, Heineken — ont déjà confié au Grup Xaluca leurs événements au cœur du désert.",
                  es: "En los últimos años, el sur de Marruecos se ha convertido en el destino elegido por miles de empresas, de todos los sectores, para sus eventos corporativos. Marcas internacionales — BMW, Vodafone, Volkswagen, Coca-Cola, IBM, Heineken — ya han confiado en Grup Xaluca para celebrar los suyos en el corazón del desierto.",
                },
                image: "https://customer-assets.emergentagent.com/job_xaluca-luxury/artifacts/4p3qhcbe_Kasbah%20Hotel%20Xaluca%20-%20Ae%CC%81reas%20-%20HamidBabni-3.jpg",
                reverse: false,
                badges: [
                  { en: "Conventions", fr: "Conventions", es: "Convenciones" },
                  { en: "Brand launches", fr: "Lancements de marque", es: "Lanzamientos de marca" },
                  { en: "Incentives", fr: "Incentives", es: "Incentivos" },
                  { en: "Leadership off-sites", fr: "Off-sites leadership", es: "Off-sites de liderazgo" },
                ],
              },
              {
                id: "celebrations",
                tag: { en: "Celebrations", fr: "Célébrations", es: "Celebraciones" },
                title: { en: "Once-in-a-lifetime gatherings", fr: "Des célébrations qui n'arrivent qu'une fois", es: "Celebraciones de una sola vez en la vida" },
                text: {
                  en: "The unique setting of southern Morocco — the beauty of its mountains, the splendour of Erg Chebbi — turns every celebration into a memory that lasts. A wedding in the heart of the dunes, an anniversary between valleys and Atlas peaks, a school reunion under the palms of an oasis. Whatever you imagine, with Grup Xaluca it is possible.",
                  fr: "Le cadre unique du sud du Maroc — la beauté de ses montagnes, la splendeur de l'Erg Chebbi — transforme chaque célébration en un souvenir qui reste. Un mariage au cœur des dunes, un anniversaire entre vallées et sommets de l'Atlas, une réunion d'anciens élèves sous les palmiers d'une oasis. Tout ce que vous imaginez, avec le Grup Xaluca c'est possible.",
                  es: "El marco único del sur de Marruecos — la belleza de sus montañas, el esplendor del Erg Chebbi — convierte cada celebración en un recuerdo que perdura. Una boda en el corazón de las dunas, un aniversario entre valles y cumbres del Atlas, una reunión de antiguos alumnos bajo las palmeras de un oasis. Todo lo que imagine, con Grup Xaluca es posible.",
                },
                image: "https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&w=1800&q=80",
                reverse: true,
                badges: [
                  { en: "Weddings", fr: "Mariages", es: "Bodas" },
                  { en: "Anniversaries", fr: "Anniversaires", es: "Aniversarios" },
                  { en: "Reunions", fr: "Retrouvailles", es: "Reuniones" },
                  { en: "Private celebrations", fr: "Célébrations privées", es: "Celebraciones privadas" },
                ],
              },
            ].map((b) => (
              <article
                key={b.id}
                className="grid lg:grid-cols-12 gap-8 lg:gap-14 items-center"
                data-testid={`events-deep-${b.id}`}
                data-cms-scope={`events.deep.${b.id}`}
              >
                <div className={`lg:col-span-6 ${b.reverse ? "lg:order-2" : ""}`}>
                  <div className="aspect-[4/3] overflow-hidden rounded-sm">
                    <img src={b.image} alt={pick(b.title, lang)} className="w-full h-full object-cover" loading="lazy" decoding="async" data-cms-role="card.image"/>
                  </div>
                </div>
                <div className={`lg:col-span-6 ${b.reverse ? "lg:order-1" : ""}`}>
                  <p className="text-[10px] tracking-[0.3em] uppercase text-[#C16542]">
                    Grup Xaluca · {pick(b.tag, lang)}
                  </p>
                  <h3 className="font-serif-x mt-3 text-3xl md:text-4xl lg:text-5xl tracking-tight leading-[1.05] text-[#2C2621]">
                    {pick(b.title, lang)}
                  </h3>
                  <p className="mt-5 text-lg text-[#5C5248] leading-relaxed">
                    {pick(b.text, lang)}
                  </p>
                  <div className="mt-6 flex flex-wrap gap-2">
                    {b.badges.map((bd) => (
                      <span
                        key={`${b.id}-badge-${bd.en}`}
                        className="text-[10px] tracking-[0.25em] uppercase px-3 py-1.5 rounded-full bg-white border border-[#E8DFD2] text-[#2C2621]"
                      >
                        {pick(bd, lang)}
                      </span>
                    ))}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CLIENTS / BRAND TRUST STRIP */}
      <section id="events-clients" className="py-16 md:py-20 bg-white border-y border-[#2C2621]/10 scroll-mt-32" data-testid="events-clients">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-4">
              <p className="overline">
                {pick({ en: "Our clients", fr: "Nos clients", es: "Nuestros clientes" }, lang)}
              </p>
              <h2 className="font-serif-x mt-3 text-3xl md:text-4xl tracking-tight leading-tight text-[#2C2621]">
                {pick({
                  en: "Brands that have already trusted us.",
                  fr: "Des marques qui nous ont déjà fait confiance.",
                  es: "Marcas que ya han confiado en nosotros.",
                }, lang)}
              </h2>
            </div>
            <div className="md:col-span-8">
              <div className="grid grid-cols-3 md:grid-cols-6 gap-y-6 gap-x-4">
                {["BMW", "Vodafone", "Volkswagen", "Coca-Cola", "IBM", "Heineken"].map((b) => (
                  <p
                    key={b}
                    className="font-serif-x text-2xl md:text-3xl text-[#2C2621]/75 text-center tracking-tight"
                    data-testid={`events-client-${b.toLowerCase()}`}
                  >
                    {b}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="events-services" className="py-24 md:py-32 bg-[#1A1513] text-[#FDFBF7] scroll-mt-32" data-testid="events-services">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-12 gap-8 mb-14 items-end">
            <div className="md:col-span-7">
              <p className="overline !text-[#D4A373]">
                {pick({ en: "Grup Xaluca · Services", fr: "Grup Xaluca · Services", es: "Grup Xaluca · Servicios" }, lang)}
              </p>
              <h2 className="font-serif-x mt-4 text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
                {pick({
                  en: "Everything your event needs — under one roof.",
                  fr: "Tout ce dont votre événement a besoin — sous un même toit.",
                  es: "Todo lo que su evento necesita — bajo un mismo techo.",
                }, lang)}
              </h2>
            </div>
            <div className="md:col-span-5">
              <p className="text-base text-[#FDFBF7]/75 leading-relaxed">
                {pick({
                  en: "Discover all the services Grup Xaluca can deliver for your organisation in the south of Morocco — kitchens, camps, fleets, permits.",
                  fr: "Découvrez tous les services que le Grup Xaluca peut fournir à votre organisation dans le sud du Maroc — cuisines, camps, flotte, autorisations.",
                  es: "Descubra todos los servicios que Grup Xaluca puede ofrecer a su organización en el sur de Marruecos — cocinas, campamentos, flota, permisos.",
                }, lang)}
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-px bg-[#FDFBF7]/15 border border-[#FDFBF7]/15 rounded-3xl overflow-hidden">
            {[
              {
                icon: Sparkles,
                tag: { en: "Grup Xaluca · Services", fr: "Grup Xaluca · Services", es: "Grup Xaluca · Servicios" },
                title: { en: "Restaurant & catering", fr: "Restauration & traiteur", es: "Restaurante y catering" },
                text: {
                  en: "Restaurant and catering services in any remote area of the desert and southern Morocco — refrigerated kitchen trucks, full mise-en-place teams, halal-certified kitchens.",
                  fr: "Services de restauration et de traiteur dans n'importe quel coin reculé du désert et du sud du Maroc — camions cuisine réfrigérés, équipes de mise en place complètes, cuisines halal certifiées.",
                  es: "Servicios de restaurante y catering en cualquier rincón remoto del desierto y del sur de Marruecos — camiones cocina refrigerados, equipos de mise-en-place completos, cocinas halal certificadas.",
                },
              },
              {
                icon: Tent,
                tag: { en: "Grup Xaluca · Services", fr: "Grup Xaluca · Services", es: "Grup Xaluca · Servicios" },
                title: { en: "Bivouac & camp service", fr: "Bivouac & camp", es: "Vivacs y campamentos" },
                text: {
                  en: "Remote camps from 1 to 1 000 people — haimas with proper beds, shower area, toilets, lighting, music, security and chef stations. Erected within 48 hours.",
                  fr: "Camps reculés de 1 à 1 000 personnes — haimas avec vrais lits, zone de douches, sanitaires, éclairage, musique, sécurité et stations chef. Montés en 48 heures.",
                  es: "Campamentos remotos de 1 a 1 000 personas — haimas con camas reales, ducha, sanitarios, iluminación, música, seguridad y estaciones de chef. Montados en 48 horas.",
                },
              },
              {
                icon: Plane,
                tag: { en: "Grup Xaluca · Services", fr: "Grup Xaluca · Services", es: "Grup Xaluca · Servicios" },
                title: { en: "Transport & logistics", fr: "Transport & logistique", es: "Transporte y logística" },
                text: {
                  en: "We have our own transport company and can manage the logistics of any event in southern Morocco — 4x4 fleet, coaches, vans, refrigerated trucks, helicopter access, full permitting.",
                  fr: "Nous possédons notre propre société de transport et pouvons gérer la logistique de tout événement dans le sud du Maroc — flotte 4x4, autocars, fourgons, camions réfrigérés, accès hélicoptère, autorisations complètes.",
                  es: "Disponemos de empresa propia de transporte y podemos gestionar la logística de cualquier evento en el sur de Marruecos — flota 4x4, autocares, furgonetas, camiones refrigerados, acceso en helicóptero y permisos completos.",
                },
              },
            ].map((s, i) => {
              const Icon = s.icon;
              return (
                <div
                  key={`events-svc-${s.tag.en}`}
                  className="bg-[#1A1513] p-8 md:p-10"
                  data-testid={`events-service-${i}`}
                >
                  <div className="w-12 h-12 rounded-full bg-[#D4A373]/15 text-[#D4A373] flex items-center justify-center">
                    <Icon className="w-5 h-5" strokeWidth={1.5} />
                  </div>
                  <p className="text-[10px] tracking-[0.3em] uppercase text-[#D4A373] mt-6">
                    {pick(s.tag, lang)}
                  </p>
                  <h3 className="font-serif-x text-2xl md:text-3xl mt-2 leading-tight text-[#FDFBF7]">
                    {pick(s.title, lang)}
                  </h3>
                  <p className="mt-5 text-[15px] leading-relaxed text-[#FDFBF7]/75">
                    {pick(s.text, lang)}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section id="events-final-cta" className="relative py-28 md:py-36 overflow-hidden scroll-mt-32" data-testid="events-final-cta" data-cms-scope="events.final-cta">
        <img
          src="https://images.pexels.com/photos/35771005/pexels-photo-35771005.jpeg?auto=compress&cs=tinysrgb&w=1920"
          alt=""
          className="absolute inset-0 w-full h-full object-cover" loading="lazy" decoding="async"
          data-cms-role="bg"/>
        <div className="absolute inset-0 bg-[#1A1513]/85" />
        <div className="relative max-w-4xl mx-auto px-6 text-center text-[#FDFBF7]">
          <p className="overline !text-[#D4A373] flex items-center gap-2 justify-center">
            <Sparkles className="w-3.5 h-3.5" strokeWidth={1.5} />
            {pick({ en: "Get in touch", fr: "Nous contacter", es: "Contáctenos" }, lang)}
          </p>
          <h2 className="font-serif-x mt-5 text-4xl md:text-6xl tracking-tight leading-[1.05]">
            {pick(COPY.cta_title, lang)}
          </h2>
          <p className="mt-6 text-lg md:text-xl text-[#FDFBF7]/85 leading-relaxed max-w-2xl mx-auto">
            {pick(COPY.cta_text, lang)}
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link
              to="/contact?subject=Events%20proposal"
              className="bg-[#C16542] hover:bg-[#A35133] text-[#FDFBF7] px-10 py-4 text-xs tracking-[0.2em] uppercase inline-flex items-center gap-2"
              data-testid="events-final-cta-primary"
            >
              {pick(COPY.cta_primary, lang)} <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
            <a
              href="mailto:xaluca@xaluca.com"
              className="border border-[#FDFBF7]/40 text-[#FDFBF7] hover:border-[#FDFBF7] px-10 py-4 text-xs tracking-[0.2em] uppercase inline-flex items-center gap-2"
              data-testid="events-final-cta-secondary"
            >
              <MapPin className="w-3.5 h-3.5" strokeWidth={1.5} />
              {pick(COPY.cta_secondary, lang)}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EventsPage;
